// The respondent grid (#1887): one row per respondent in data order, the case
// column first, then the pulled-in variables. Pages of at most 500 rows, so a
// plain table with a sticky header; no virtualisation.
import React from 'react'
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
  CASE_DATA_PAGE_SIZES,
  formatCaseId,
  formatCaseValue,
  type CaseDataViewPage,
} from '../../utils/caseDataView'

interface CaseDataGridProps {
  page: CaseDataViewPage
  showCodes: boolean
  isLoading: boolean
  /** Rows per page as chosen; the page shown may still be the last one fetched. */
  limit: number
  onRemoveColumn: (variable: string) => void
  onPage: (offset: number) => void
  onLimit: (limit: number) => void
}

const fmt = (n: number) => n.toLocaleString('en-GB')

const CaseDataGrid: React.FC<CaseDataGridProps> = ({ page, showCodes, isLoading, limit, onRemoveColumn, onPage, onLimit }) => {
  const { rows, columns, offset, filtered_cases: filtered, total_cases: total } = page
  const first = rows.length > 0 ? offset + 1 : 0
  const last = offset + rows.length
  const caseHeader = page.case_variable || 'Case'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ height: 2 }}>{isLoading && <LinearProgress color="info" sx={{ height: 2 }} />}</Box>
      <TableContainer sx={{ flex: 1, minHeight: 0 }}>
        <Table size="small" stickyHeader aria-label="Respondent data">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                <Tooltip title={page.case_variable ? 'The case variable' : 'No case variable found: the position of the respondent in the data'}>
                  <span>{caseHeader}</span>
                </Tooltip>
              </TableCell>
              {columns.map((col) => (
                <TableCell key={col.name} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={col.description || col.name}>
                      <span>{col.name}</span>
                    </Tooltip>
                    <IconButton size="small" aria-label={`Remove ${col.name}`} onClick={() => onRemoveColumn(col.name)} sx={{ p: '2px' }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.index} hover>
                <TableCell sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{formatCaseId(row.case)}</TableCell>
                {columns.map((col, i) => (
                  <TableCell
                    key={col.name}
                    sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(col.data_type === 'number' ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } : {}) }}
                  >
                    {formatCaseValue(row.values[i], col, showCodes)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && !isLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            No respondents match this filter.
          </Typography>
        )}
      </TableContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} data-testid="case-data-pager">
          {filtered === total
            ? `Rows ${fmt(first)}–${fmt(last)} of ${fmt(total)}`
            : `Rows ${fmt(first)}–${fmt(last)} of ${fmt(filtered)} filtered (${fmt(total)} in all)`}
        </Typography>
        <Select
          size="small"
          value={limit}
          onChange={(e) => onLimit(Number(e.target.value))}
          inputProps={{ 'aria-label': 'Rows per page' }}
        >
          {CASE_DATA_PAGE_SIZES.map((n) => <MenuItem key={n} value={n}>{n} rows</MenuItem>)}
        </Select>
        <Button size="small" color="info" disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - limit))}>
          Previous
        </Button>
        <Button size="small" color="info" disabled={last >= filtered} onClick={() => onPage(offset + limit)}>
          Next
        </Button>
      </Box>
    </Box>
  )
}

export default CaseDataGrid
